import { useLatestProductVersion } from '../../../utility/TreeHelpers'
import { nextVersionName } from '../../TreeActions'
import { IVersionUpdateData } from '../MasterIdSelection'
import { getMasterUpdateValidations } from '../../../masters_management/utility'
import { getSortedByValue } from 'shared/utils/sorting'
import { useAtom } from 'infrastructure/state/jotai.wrapper'
import { useGetProductForId } from 'shared/api/tree'
import { useCallback, useMemo } from 'react'
import { selectedUpdatesAtom } from './atoms'
import { useGetTree } from 'shared/hooks'
import { getAPIRequest, IMasterCurricula } from 'infrastructure/api'
import { deepMapKeysToCamel } from 'shared/utils/object'

export function useGetUpdateData() {
  const versionGet = useLatestProductVersion()
  const getProduct = useGetProductForId()
  const getTree = useGetTree()

  return useCallback(
    async (productId: number) => {
      const product = getProduct(productId)
      if (!product?.editable_tree || !product.primary_tree_tree_id) return
      const latestMaster = versionGet(productId)
      if (!latestMaster) return
      const newVersionName = nextVersionName(latestMaster.name)

      const { tree_id: treeId, version_id: versionId } = product.editable_tree

      const tree = await getTree({ treeId, versionId })
      if (!tree) return

      const updateData: IVersionUpdateData = {
        productId,
        newTree: tree,
        newVersionName,
        treeId: product.primary_tree_tree_id,
        versionId: latestMaster.version,
        fromTreeTitle: tree.title,
        toTreeTitle: latestMaster.title,
        valid: true,
        validations: getMasterUpdateValidations(tree),
      }
      return updateData
    },
    [getTree, getProduct, versionGet],
  )
}

async function fetchFreshMastersById() {
  const resp = await getAPIRequest<
    {
      created_at: string
      name: string
      title: string
      tree_id: string
      updated_at: string
      version: string
    }[]
  >('/api/tree/masters')
  const masters = resp.map((m) => deepMapKeysToCamel<IMasterCurricula>(m))
  return getSortedByValue<IMasterCurricula>(masters, ['treeId', 'createdAt'])
}

export function useUpdatesActions() {
  const [updates, setUpdates] = useAtom(selectedUpdatesAtom)
  const getter = useGetUpdateData()
  const getProduct = useGetProductForId()

  return useMemo(() => {
    return {
      refreshAllUpdates: async () => {
        const freshMasters = await fetchFreshMastersById()
        let refreshed: IVersionUpdateData[] = []
        setUpdates((prev) => {
          refreshed = prev.map((update) => {
            const product = getProduct(update.productId)
            if (!product?.primary_tree_tree_id) return update
            const latest = freshMasters[product.primary_tree_tree_id]?.[0]
            if (!latest) return update
            return {
              ...update,
              newVersionName: nextVersionName(latest.name),
              versionId: latest.version,
            }
          })
          return refreshed
        })
        return refreshed
      },
      addUpdate(updateData: IVersionUpdateData) {
        setUpdates((prev) => {
          if (prev.some((u) => u.productId === updateData.productId))
            return prev
          return [...prev, updateData]
        })
      },
      removeUpdateAtIndex(index: number) {
        setUpdates((prev) => {
          prev.splice(index, 1)
          return [...prev]
        })
      },
      refreshAtIndex: async (index: number) => {
        const update = updates[index]
        const newUpdateData = await getter(update.productId)
        if (!newUpdateData) return

        setUpdates((prev) => [
          ...prev.slice(0, index),
          newUpdateData,
          ...prev.slice(index + 1),
        ])
      },
    }
  }, [updates, setUpdates, getter, getProduct])
}
